import React, { useCallback, useEffect, useRef, useState } from "react";
import {
    ADVANCED_SEARCH_MIN_MESSAGE,
    BASIC_SEARCH_MIN_MESSAGE,
    CLASSIC_ENTRY_ZIP_MAX_LENGTH,
    CLASSIC_PHONE_REGEX,
    CLASSIC_ZIP_REGEX,
    INVALID_CITY_MESSAGE,
    INVALID_PHONE_MESSAGE,
    INVALID_STATE_MESSAGE,
    INVALID_ZIP_MESSAGE,
    QUICK_ENTRY_CITY_REGEX,
    QUICK_ENTRY_FIELD_MAX_LENGTH,
    REQUESTOR_SEARCH_MAX_LENGTH,
    QUICK_ENTRY_STATE_REGEX,
} from "./quickEntryFieldConstraints";
import { UNITED_STATES } from "../../../models/misc";
import { FieldGroupRow, FieldGroupRowStatus, FieldGrounding } from "../../../models/intake";
import QuickEntryGroundingIcon from "./QuickEntryGroundingIcon";
import {
    DEFAULT_REQUESTOR_MATCH_STATE,
    RequestorMatchApiPayload,
    RequestorMatchState,
    mapRequestorMatchState,
    buildRequestorMatchTooltip,
    buildSelectedRequestorTooltip,
    flattenMatchTooltip,
} from "../matchState";
import MatchTooltipBody, { MatchInfoTooltip } from "./MatchTooltipBody";
import MatchGridModal, { MatchGridColumn, MatchGridRow } from "./MatchGridModal";
import InlineSearchForm from "./InlineSearchForm";
import CreateEntityFormModal, { CreateEntityField } from "./CreateEntityFormModal";
import { hasDbMatch, shouldPrefillCreateForm } from "./createPrefill";
import { DDS_SITE_CODE_MAX_LENGTH } from "./DdsFieldsGroup";
import "./RequestorInfoRow.scss";

export const REQUESTOR_INFO_ROW_ID = "requestorInfo";

/**
 * Shown on a resolved requestor that has no contact yet. Deliberately the SAME copy
 * Classic shows under its Contacts header (localValidations.ts:246 /
 * DataEntryDetailsRightPane setRequiredContactError) so the two modes read identically.
 */
export const CONTACT_SELECTION_REQUIRED_MESSAGE = "A contact selection must be made.";

/**
 * AB#39052: the identity of a requestor candidate, for the React key, the radio-card
 * selection, and the lookup that resolves a Confirm back to its candidate. All three MUST
 * use this - they are matched against each other.
 *
 * It is namespaced rather than the bare id because a candidate carries a contactId only
 * when exactly one of its requestor's contacts came through the search; a requestor that
 * matched through several carries none, and keys on its requestorId instead. A bare
 * `contactId ?? requestorId` therefore mixes two id domains in one key space, and two
 * candidates collide the moment one requestor's id equals another's contact id.
 *
 * That is not hypothetical. Measured 2026-09-08: on aspDev and aspTest the two IDENTITY
 * ranges happen not to overlap (RequestorNameID <= 502,878,064 against RequestorContactID
 * >= 1,000,000,284), but on aspRC ALL 342 requestor ids are also contact ids, and seven
 * real fax searches there return a candidate list that mixes both key kinds. Nothing
 * enforces the separation - there is no constraint, only two IDENTITY seeds - so the
 * disjointness is a property of two databases, not an invariant.
 *
 * A collision selects and confirms the WRONG requestor silently: the lookup takes the
 * first match, so clicking one card applies the other, and both render as selected.
 */
export const requestorCandidateKey = (c: { readonly requestorId?: number; readonly contactId?: number }): string =>
    c.contactId != null ? `c${c.contactId}` : `r${c.requestorId ?? ""}`;

/**
 * AB#39052: the address is what tells two requestors apart. Every other field is shared by
 * definition - these candidates are here BECAUSE they matched the same fax or email, and the
 * badges and prior-request count are requestor-level. City alone was not enough: two
 * "PDC Retrievals" records on aspTest share a fax and differ only by street address.
 *
 * Street and locality on ONE line, so a three-candidate list stays scannable. Module-level so
 * the inline cards and the results grid render the same string from the same code.
 */
export const candidateAddressLine = (c: {
    readonly address1?: string;
    readonly address2?: string;
    readonly city?: string;
    readonly state?: string;
    readonly zipCode?: string;
}): string =>
    [c.address1, c.address2, c.city, [c.state, c.zipCode].filter(Boolean).join(" ")]
        .filter(Boolean).join(", ");

/**
 * AB#39052: the contact fields the requestor search no longer returns (Ali Moll,
 * PR !10513 thread 56655). The search is requestor-level - it returns a contact ID and
 * a count - so these are fetched by id for the ONE contact a row resolves to, through
 * the parent's `resolveContact`.
 */
export interface RequestorContactDemographics {
    readonly contactFirstName?: string;
    readonly contactLastName?: string;
    readonly email?: string;
    readonly phone?: string;
    readonly fax?: string;
}

export interface RequestorSearchKeys {
    readonly fax?: string;
    readonly email?: string;
    readonly name?: string;
    readonly org?: string;
    // Spec #35136 (F1f): DDS matches on the barcode's RQ site code instead of fax/email.
    readonly siteCode?: string;
}

/** Create-new requestor values captured by the no-match create form (AC9) - the org + address + contact fields classic's add-new-requestor form captures. */
export interface RequestorCreateValues {
    readonly organization: string;
    readonly address1: string;
    readonly address2: string;
    readonly city: string;
    readonly state: string;
    readonly zip: string;
    readonly contactFirstName: string;
    readonly contactLastName: string;
    readonly email: string;
    readonly phone: string;
    readonly fax: string;
}

/**
 * A requestor picked from the classic results grid in the requestor modal (AB#35116).
 * Carries the DB identity plus the row's display values, so the QE row can show what
 * was selected instead of the AI-extracted guess.
 */
export interface ModalRequestorSelection extends RequestorCreateValues {
    readonly requestorId?: number;
    readonly contactId?: number;
    readonly eTransfer?: boolean;
    readonly national?: boolean;
}

export interface RequestorInfoRowProps {
    /** AI-extracted keys from redux `requestAnalysis` (F1a) - undefined/empty until extraction completes (AC13). */
    readonly extractedFax?: string;
    /** AB#35243: extracted requestor contact phone, distinct from the fax key. */
    readonly extractedPhone?: string;
    readonly extractedEmail?: string;
    readonly extractedName?: string;
    readonly extractedAddress1?: string;
    readonly extractedCity?: string;
    /** Additional AI-extracted fields used only to pre-fill the create-new form (not search keys). */
    readonly extractedAddress2?: string;
    readonly extractedState?: string;
    readonly extractedZip?: string;
    readonly extractedContactFirstName?: string;
    readonly extractedContactLastName?: string;
    /** Calls the stateless POST /requestor-search endpoint (AC2/AC4) - same function for automated match and INLINE re-search. */
    readonly search: (keys: RequestorSearchKeys) => Promise<RequestorMatchApiPayload>;
    /**
     * AB#39052: reads ONE contact's demographics by id (Ali Moll, PR !10513 thread 56655).
     *
     * The search stopped returning them: it is a requestor-level search, and a first/last
     * name, email, phone or fax could not differentiate candidates that are in the list
     * BECAUSE they matched the same fax or email. So the row asks for them once, for the
     * single contact it has actually resolved to - the automated single match, or the
     * candidate the specialist confirmed.
     *
     * Optional, and failure is non-fatal: without it the row still carries `contactId`,
     * which is what links the contact and what the amber gate reads, and the specialist
     * can still open Change Contact. The send-to name/email/phone/fax simply stay blank.
     */
    readonly resolveContact?: (contactId: number) => Promise<RequestorContactDemographics | undefined>;
    /**
     * "Create New" (AC9 no-match path): the specialist filled the create-new
     * form (pre-filled with the AI-extracted values when no DB match was found -
     * AB#38824, createPrefill.ts - then edited). The values
     * are written into the data-entry form by the parent - this component does
     * NOT persist to the database; the transaction's final Save persists them.
     */
    /**
     * AB#38938: `hadDbMatch` says whether a DATABASE match was on this row when the specialist
     * chose to create instead - and, being sticky, whether one ever was for the create sequence
     * this row is in (see declinedDbMatchRef).
     *
     * The parent needs it because the requestor's CONTACT comes from the same document. When a
     * match was declined, the document's contact belongs to the organisation that was rejected.
     *
     * Reported from HERE rather than re-derived in the parent, though the parent could re-derive
     * the FIRST create: `onCreateRequestor` is this handler's first statement and the row's
     * match-state report is keyed only on status/confidence, so the parent's own
     * `requestorMatchState` still reads `matched, userEntered: false` at that moment. It is
     * reported from the row because only the row can be sticky across repeat creates, and because
     * a form opened while a search was still in flight is a case the parent's snapshot gets wrong.
     */
    readonly onCreateRequestor?: (values: RequestorCreateValues, hadDbMatch: boolean) => void;
    /**
     * AC9 resolved-existing-match path (F1c coalesce 2026-07-20 fix): fired when
     * the row resolves to an EXISTING requestor (automated single/green match or
     * a grid pick). Carries the matched requestor's identity so the parent writes
     * it into the requestor form + SEND_TO_* billing mirrors, mirroring
     * onCreateRequestor. Before this a picked/auto-matched requestor lived only
     * in local apiResult and was lost on unmount, so the committed transaction
     * never carried the matched requestor. NOT fired for create-new. NOTE: the
     * single-match payload only surfaces org/address1/city today (the contact/
     * fax columns are a separate open proc gap) - only those are written.
     */
    readonly onSelectRequestor?: (values: RequestorCreateValues, requestorId: number | undefined, contactId: number | undefined, source: "auto" | "manual") => void;
    /**
     * Reports this row's computed match state UP to the parent whenever it
     * changes, so the parent (DataEntryDetails.getQuickEntryRows) can drive the
     * Quick Entry numbered-list BADGE icon from the REAL match result. Without
     * this the parent only had a placeholder (isSearching hardcoded true) and
     * the badge was stuck on amber/LowConfidence once keys existed. Fired from
     * a useEffect keyed on the primitive status/confidence, so it never loops.
     */
    readonly onMatchStateChange?: (matchState: RequestorMatchState) => void;
    /**
     * Opens the classic Requestor/Contact modal (AB#35116 Phase 1) for full
     * search / select / assign-contact / create-new. The row keeps its AI
     * auto-match summary; this is the affordance into the classic-parity surface.
     *
     * `keys` carries what the specialist actually has in the inline boxes at click
     * time, so the modal searches on THAT and not on the AI-extracted values. An
     * empty string is meaningful (the box was cleared) - don't treat it as absent.
     * `siteCode` is the DDS row's inline search variant's third field (Spec #35136
     * F1f) - undefined for non-DDS, and for DDS a plain Reset (no typed override)
     * also leaves it undefined so the modal searches on whatever is already in
     * the form rather than blanking the barcode-scanned site code.
     */
    readonly onOpenRequestorModal?: (keys?: {
        readonly fax?: string;
        readonly email?: string;
        readonly siteCode?: string;
        // AB#39478: Classic's Advanced Search keys. The modal's pane already sends all four to
        // the classic search - these carry what the specialist typed into its redux-form fields.
        readonly address?: string;
        readonly zip?: string;
        readonly requestorName?: string;
        readonly contactName?: string;
    }) => void;
    /**
     * AB#35116: bumped by the parent when the classic results grid's "Add new
     * Requestor" is clicked, so the row opens its OWN create-requestor modal
     * (document-prefilled) instead of the classic inline form, which isn't visible
     * in the chromeless host. A counter, not a boolean: re-clicking must reopen.
     */
    readonly createOpenSignal?: number;
    /**
     * AB#35116: the requestor the specialist picked in the classic results grid. Set by
     * the parent when the modal reports a pick; the row then displays that requestor and
     * resolves to it (matched/Single), which also drives the form + send-to writes
     * through the existing onSelectRequestor path.
     */
    readonly selectedRequestor?: ModalRequestorSelection;
    /**
     * AB#35119 (reopened): the server's message when Quick Entry's immediate contact save was
     * refused - e.g. "Email domain 'nefarious.com' is not allowed…". Rendered on the row, next to the
     * contact that caused it, because the alternative surface is the full-page "Service is temporarily
     * unavailable" banner, which reads as an outage rather than a value the specialist can correct.
     */
    readonly contactSaveError?: string;
    /**
     * AB#31605 (reopen rehydration): a "matched single" apiResult pre-built from
     * the transaction's PERSISTED requestor link (buildPersistedRequestorMatch).
     * Only used when `preferPersistedMatch` is true. FULL-ONLY - undefined until
     * the id-matched requestor detail record has loaded (so pre-resolve never
     * freezes a name-only record into the send-to form, AB#35081).
     */
    readonly persistedMatch?: RequestorMatchApiPayload;
    /**
     * AB#31605 (reopen rehydration): true on a plain reopen (the tx has a saved
     * requestor link and NO fresh extraction happened this session). The parent
     * owns this decision because the row can't tell a stale re-fetched AI key from
     * a fresh one. When true the row pre-resolves from `persistedMatch` and
     * SUPPRESSES the automated search; when false it ignores `persistedMatch` and
     * searches the extraction keys as usual.
     */
    readonly preferPersistedMatch?: boolean;
    /**
     * AB#31605 (reopen rehydration MINOR-A/B): true while a persisted link is
     * EXPECTED but not yet resolved - either transactionDetails hasn't loaded so
     * the link status is unknown (the stale-requestAnalysis-lands-first race), or
     * the link exists and its detail record is still loading. While true the row
     * HOLDS the automated search and shows a transient "matching" state; the parent
     * releases it once the record resolves OR the load settles with no record.
     */
    readonly awaitingPersistedMatch?: boolean;
    /**
     * Spec #35136 (F1f): the DDS (Disability/SSA) barcode's RQ site code. When
     * present (a DDS transaction whose barcode has landed), the automated-search
     * effect keys the SAME stateless /requestor-search endpoint on siteCode
     * instead of fax/email — so DDS resolves through the identical intake-match
     * pipeline: Single pins its contact via the write-once onSelectRequestor
     * effect (green when the proc returns a real contactId, amber only when
     * genuinely absent); Multiple renders the inline candidate radio-cards; None
     * renders inline no-match. Suppressed while a reopen persisted match is
     * preferred/awaited (never fights AB#31605).
     */
    readonly ddsSiteCode?: string;
    /**
     * Spec #35136 (F1f) fix: true for any DDS transaction (regardless of whether
     * ddsSiteCode currently resolves to a value - it goes undefined once a
     * requestor is linked, see resolveDdsSiteCode). Reset/search-manually still
     * follow the SAME reveal-then-search two-step every row uses (setSearchMode);
     * this only picks which InlineSearchForm variant renders - DDS gets Site
     * Code/Fax/Email instead of just Fax/Email, since Classic's own DDS search
     * bundles all three (and more) into one call, not just the site code.
     */
    readonly isDdsRequest?: boolean;
    /**
     * Spec #35136 (F1f) fix: the CURRENT RQ Site Code, regardless of resolution
     * state - unlike `ddsSiteCode` (which goes undefined once a requestor links,
     * so the auto-search effect stops re-firing), this pre-fills the DDS inline
     * search box's Site Code field so re-searching starts from what's actually in
     * the form rather than a blank box.
     */
    readonly currentDdsSiteCode?: string;
    /**
     * AB#35066: per-value grounding (≡) affordance for DOC-SOURCED requestor
     * sub-values. Only the CREATED-requestor path has FROM DOC fields (Address/
     * City/Contact/Fax/Email prefilled from the extraction); a matched requestor
     * is all FROM DATABASE. Wired to the parent's reveal/no-source handlers;
     * degrades to the informational toast where no grounding coord exists (#34112).
     */
    readonly fieldGroundings?: readonly FieldGrounding[];
    readonly onRevealGrounding?: (grounding: FieldGrounding) => void;
    readonly onGroundingUnavailable?: () => void;
    /**
     * Assign (or change) the CONTACT on the resolved requestor - opens the contacts
     * popup. Takes no identity: the parent owns the resolved requestor id
     * (quickEntryRequestorResolution), which is authoritative for all three resolve
     * paths (auto-match, grid pick, create-new - the last only gets its id back from
     * the create POST, so the row never sees it). This just signals intent.
     */
    readonly onAssignContact?: () => void;
    /**
     * The contact assigned through that popup. Displayed over whatever contact the
     * requestor record or the document carried, on every resolved display - so a
     * created / auto-matched requestor shows its assigned contact too, not just one
     * picked from the classic grid.
     */
    readonly assignedContact?: {
        readonly contactFirstName: string;
        readonly contactLastName: string;
        readonly email: string;
        readonly phone: string;
        readonly fax: string;
    };
    /**
     * Direct dev report: for a DDS (Disability/Social Security) request the DDS
     * barcode fields and this row's search both fall under ONE "Requestor Info"
     * heading (DataEntryDetails.tsx renders that heading once, above both), so
     * this row's own internal label would be a second, redundant "Requestor
     * Info" line sitting between them. Defaults to true (unchanged) for every
     * other caller/request type.
     */
    readonly showLabel?: boolean;
}

/**
 * Requestor Info Quick Entry row (Spec #34101 (F1c), badge 2). Shows the
 * e-transfer indicator (AC8) and a display-only "N prior requests"
 * (AC7/TransactionCount) alongside the standard match-confidence states.
 *
 * Per Lyn O'Connor's 2026-07-17 decision the SEARCH FIELDS are INLINE on the
 * row (fax + email tagged PRIMARY, plus optional name/org), pre-filled with
 * the AI-extracted values. The RESULTS/disambiguation MODAL is now the
 * filterable candidate GRID (MatchGridModal, AC9 / master-demo-v6 Requestor
 * Search) - it opens automatically when a search returns MULTIPLE candidates,
 * showing ORGANIZATION (with e-transfer/national badges) | ADDRESS | PRIOR, a
 * per-column filter row, row-select, and "+ Create New Requestor".
 * [Search] on a matched row re-searches inline; when multiple candidates exist
 * the row also offers "Review matches" to reopen the results grid.
 */
const RequestorInfoRow: React.FC<RequestorInfoRowProps> = (props) => {
    const [apiResult, setApiResult] = useState<RequestorMatchApiPayload | undefined>(undefined);
    // AB#35289 (source-flagged reset): how the CURRENT apiResult was produced - an AI
    // automated match ("auto") vs a deliberate operator action ("manual": a manual inline
    // search, a grid pick, or a rehydrated committed prior choice). Threaded to
    // onSelectRequestor so the parent can re-evaluate an "auto" requestor when the request
    // type flips to Internal while PRESERVING deliberate picks. A ref (not state) so it is
    // read at effect-fire time without re-render churn or an extra effect dependency.
    const apiResultSourceRef = useRef<"auto" | "manual">("auto");
    // AB#35289 (root-cause hardening, round-5 finding 4): the ONE place apiResult is set.
    // It ALWAYS requires an explicit source and sets apiResult + apiResultSourceRef together,
    // so no current/future path can silently inherit the "auto" default (the round-5 CRITICAL was
    // exactly a setApiResult site - the classic grid-pick effect - that forgot to tag "manual").
    // Every set of apiResult MUST go through here. `next` may be a value or a functional updater
    // (matching setState). For a cleared/unmatched result the source is inert (the onSelectRequestor
    // effect fires only for a matched Single), so callers clearing the row pass "auto".
    const applyApiResult = (
        next: React.SetStateAction<RequestorMatchApiPayload | undefined>,
        source: "auto" | "manual",
    ) => {
        apiResultSourceRef.current = source;
        setApiResult(next);
    };
    const [isSearching, setIsSearching] = useState(false);
    const [serviceUnavailable, setServiceUnavailable] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    // Reveals the inline search form on a matched row for re-search via [Search].
    const [searchMode, setSearchMode] = useState(false);
    // AB#39478: a refused search (too few keys). Previously the row returned silently, so a
    // specialist who clicked Search with an empty form got no feedback at all.
    const [searchError, setSearchError] = useState("");
    // Opens the create-new requestor form (AC9 no-match path).
    const [createOpen, setCreateOpen] = useState(false);
    /**
     * AB#38824: whether THIS opening of the create form carries the document's values, latched at
     * the moment it opens rather than read live.
     *
     * The modal seeds its inputs once, in a lazy `useState` initializer, and never re-reads
     * `fields` - so the VALUES are frozen at mount whatever happens afterwards. The subtitle is
     * not. Reading the rule live would let a form that opened in `awaitingDocument` (pre-filled,
     * correctly) have its subtitle flip to "Enter the new requestor's details" the instant the
     * extraction lands and the automated match resolves - a dialog contradicting its own boxes.
     * One latch, set where the form opens, drives both, so they cannot disagree.
     */
    const [createPrefills, setCreatePrefills] = useState(true);

    /**
     * AB#38938: STICKY. True once the specialist has created a requestor on this row after
     * declining a DB match, and it stays true for any further create on the same row.
     *
     * Without the stickiness the fix survives exactly one click. `onCreateSubmit` synthesizes a
     * matched result with `userEntered` set so the row can display the new requestor, and that
     * masks the DB match from `hasDbMatch` - so a SECOND "Add new Requestor" on the same row
     * reported "no match was declined" and the contact pre-fill came back. Two clicks, through
     * the same door the bug was reported through, and a specialist who mistypes the first entry
     * hits it. Measured before this ref existed: the two creates reported [true, false].
     *
     * Reset by REMOUNT, and deliberately by nothing else. The parent keys this row on
     * `quickEntryRequestorResetGeneration` (and the pane on `quickEntryIntakeGeneration`), so an
     * Internal type switch, a document replace and advancing to the next transaction each give the
     * row a fresh ref - which is exactly the scope over which "a match was declined here" should
     * hold.
     *
     * Explicit clearing was tried and removed as dead code. `runSearch` cannot run after a create
     * (the automated-match effect returns early while `created` is set), and clearing it when the
     * row resolves to an existing requestor changes nothing: `hasDbMatch` is then true on its own,
     * so a create after that reports true anyway - correctly, because the specialist is declining
     * THAT match.
     */
    const declinedDbMatchRef = useRef(false);
    // Holds the specialist-entered values after "Create New" so the row renders
    // the created requestor (name + address/contact, blue user-entered badge)
    // instead of an AI match. Cleared whenever a fresh search runs.
    const [created, setCreated] = useState<RequestorCreateValues | undefined>(undefined);
    // Requestor picked in the classic results grid (AB#35116). Same rich display as
    // `created`, but sourced FROM DATABASE rather than typed. Cleared by a fresh search.
    const [selected, setSelected] = useState<ModalRequestorSelection | undefined>(undefined);
    // Selected candidate in the Multiple radio-card list (defaults to the top
    // pre-selected candidate); cleared on a fresh search.
    const [selectedCandidateId, setSelectedCandidateId] = useState<string | number | undefined>(undefined);
    // AB#35087: candidate count when the specialist tie-broke a MULTIPLE result (undefined for a clean single / fresh search).
    const [selectedFromCount, setSelectedFromCount] = useState<number | undefined>(undefined);
    // True only while a MANUAL inline re-search (typed values, not the
    // automated match) is in flight. Keeps the inline search form mounted
    // through the transient "matching" status - without this, `matching`
    // isn't in showInlineSearch's status list, so the form unmounts mid-search
    // and (on resolution) remounts keyed on props.extractedFax/extractedEmail,
    // discarding whatever the specialist just typed and reverting the fields
    // back to the original AI-extracted values.
    const [manualSearchPending, setManualSearchPending] = useState(false);

    // AB#37842: runSearch awaits props.search(keys) and then sets several pieces
    // of state - if the row unmounts while that promise is in flight (removed
    // from a re-rendered list, or a document REPLACE firing a new search before
    // the old one resolves), the eventual resolve/reject called setState on a
    // dead component, logging React's "Can't perform a React state update on an
    // unmounted component" warning. Guarded by a mounted ref rather than an
    // AbortController - runSearch has no cancellation contract with props.search,
    // and the fix only needs to stop acting on a stale result, not cancel the
    // in-flight request itself.
    const isMountedRef = useRef(false);
    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    /**
     * AB#39052: fill in the pinned contact's demographics before the payload is applied.
     *
     * Awaited BEFORE applyApiResult, not merged into it afterwards, and that is the point:
     * apiResult drives the write-once onSelectRequestor effect, so a payload that landed
     * first without a contact name and gained one a moment later would have already written
     * a blank send-to and latched its key against re-writing. Resolving first means the row
     * has one state, not two.
     *
     * A failure resolves to the payload unchanged - see the `resolveContact` prop.
     */
    const withResolvedContact = async (
        payload: RequestorMatchApiPayload | undefined,
    ): Promise<RequestorMatchApiPayload | undefined> => {
        const contactId = payload?.contactId;
        if (!payload || contactId == null || !props.resolveContact) {
            return payload;
        }
        try {
            const contact = await props.resolveContact(contactId);
            return contact ? { ...payload, ...contact } : payload;
        } catch {
            return payload;
        }
    };

    const runSearch = useCallback(async (keys: RequestorSearchKeys, opts?: { manual?: boolean }) => {
        setIsSearching(true);
        setServiceUnavailable(false);
        setSearchMode(false);
        setCreated(undefined);
        setSelected(undefined);
        setSelectedCandidateId(undefined);
        setSelectedFromCount(undefined); // AB#35087: fresh search clears any prior tie-break count
        if (opts?.manual) {
            setManualSearchPending(true);
        }
        try {
            const result = await props.search(keys);
            // AB#39052: one extra by-id read when the search pinned a contact, awaited here so
            // the row never applies a resolved requestor with a half-populated contact.
            const resolved = await withResolvedContact(result);
            if (!isMountedRef.current) {
                return;
            }
            // AB#35289: an automated match is "auto"; a MANUAL inline Search (opts.manual)
            // is a deliberate operator action -> "manual" (it also opens the grid to pick).
            applyApiResult(resolved, opts?.manual ? "manual" : "auto");
            setIsSearching(false);
            setManualSearchPending(false);
            // A MANUAL inline Search opens the filterable results grid (classic
            // parity: shows for ANY count, including the empty state). The
            // AUTOMATED match does NOT open the modal - a Multiple result renders
            // INLINE as a pre-selected radio-card list (master-demo-v6); single ->
            // green inline, zero -> inline no-match (AC9/AC10).
            if (opts?.manual) {
                setModalOpen(true);
            }
        } catch {
            if (!isMountedRef.current) {
                return;
            }
            // Cleared/unmatched -> the onSelectRequestor effect never fires, so the source is
            // inert here; pass "auto" to satisfy applyApiResult's explicit-source contract.
            applyApiResult(undefined, "auto");
            setIsSearching(false);
            setServiceUnavailable(true);
            setManualSearchPending(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.search]);

    // Automated match (AC4): fires once extraction supplies fax/email. A
    // document REPLACE re-extracts and changes these props, re-running this
    // effect with the new keys (and, via runSearch, clearing prior state) -
    // an APPEND leaves them unchanged so this effect does not re-fire
    // (AC14/Business Rule 10). AB#31605: SUPPRESSED on a plain reopen
    // (preferPersistedMatch) - the re-fetched AI keys are STALE, so searching them
    // would re-search / re-pick over the persisted link; the pre-resolve effect
    // below restores the saved requestor instead. Once a fresh extraction fires,
    // preferPersistedMatch is false and this searches the new keys as before.
    useEffect(() => {
        // Also held while awaitingPersistedMatch (AB#31605 MINOR-A): on reopen the
        // stale re-fetched requestAnalysis can land BEFORE transactionDetails, so
        // the parent holds the search until the persisted-link status is known -
        // otherwise this would fire off stale keys and re-search a saved requestor.
        if (props.preferPersistedMatch || props.awaitingPersistedMatch) {
            return;
        }
        // Once this row already has a RESOLVED requestor - created, a grid/modal pick,
        // an inline candidate pick, or a confirmed single match - do NOT auto-search
        // again. Fix for the DDS regression: when ddsSiteCode clears to undefined after a
        // requestor is linked (resolveDdsSiteCode's hasLinkedRequestor guard), this effect
        // re-ran and, finding no siteCode, fell through to the fax/email fallback (the
        // AI-extracted fax is still present) - firing an unwanted search that OVERWROTE the
        // just-confirmed match with an empty result (row went red), and the next Search
        // re-ran the heavy 50-candidate site-code search. A legitimate re-search is
        // unaffected: a document replace REMOUNTS this row (clearing all of these), and a
        // manual inline re-search calls runSearch directly rather than through this effect.
        if (created || selected || props.selectedRequestor || selectedCandidateId !== undefined
            || (apiResult?.matched && apiResult.confidence === "Single")) {
            return;
        }
        // Spec #35136 (F1f): DDS matches on the barcode's RQ site code through the
        // SAME stateless search - a distinct, mutually-exclusive key from fax/email.
        // The result lands in apiResult exactly like the fax/email match, so the
        // single/multiple/none rendering + onSelectRequestor write are all reused.
        if (props.ddsSiteCode) {
            runSearch({ siteCode: props.ddsSiteCode });
        } else if (props.extractedFax || props.extractedEmail) {
            // AB#35092: the AUTOMATED match deliberately sends fax/email ONLY - no name.
            // ROIS_IntakeRequestorSearchQuickEntry_sel ANDs the name into the query
            // (`IF @Name > '' ... AND rn.Name LIKE '%' + @Name + '%'`), so passing the
            // AI-extracted name over-constrained the search: a purely cosmetic difference
            // between the extracted name and the stored one ("Lakeside Family Medicine" vs
            // "Lakeside Family Medicine, Inc.") filtered out an otherwise-correct FAX match and
            // the row reported "No match found". The PRD defines the automated match as fax
            // primary / email secondary - the name was never meant to be a filter here.
            // The INLINE/manual re-search still passes a name, because there the specialist
            // typed it deliberately.
            runSearch({ fax: props.extractedFax, email: props.extractedEmail });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.extractedFax, props.extractedEmail, props.ddsSiteCode, props.preferPersistedMatch, props.awaitingPersistedMatch]);

    // Pre-resolve from the transaction's PERSISTED requestor link on a plain
    // reopen (AB#31605): when the parent says to prefer the saved link (reopen, no
    // fresh extraction) and the full persisted record has loaded, seed apiResult
    // from it so the row shows the resolved requestor without re-searching.
    // persistedMatch is FULL-ONLY (buildPersistedRequestorMatch returns undefined
    // until the id-matched requestor record loads), so this fires once with
    // complete data and the onSelectRequestor write-once effect below writes the
    // send-to form once with complete data - never a name-only freeze (AB#35081).
    // Latched via a ref so a later manual re-search that clears apiResult can't
    // snap the row back to the persisted record. A fresh extraction remounts this
    // row (quickEntryIntakeGeneration), resetting the latch.
    const preResolvedRef = useRef(false);
    useEffect(() => {
        if (preResolvedRef.current) {
            return;
        }
        if (props.preferPersistedMatch && props.persistedMatch && !apiResult && !created) {
            preResolvedRef.current = true;
            // AB#35289: a rehydrated committed prior choice is "manual" - never auto-reset it.
            applyApiResult(props.persistedMatch, "manual");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.preferPersistedMatch, props.persistedMatch]);

    const hasKeys = Boolean(props.extractedFax || props.extractedEmail);
    // Reopen window: the parent prefers the persisted link but the full record
    // hasn't resolved into apiResult yet - show a transient "matching" state
    // rather than flashing the no-match inline search off the suppressed keys.
    const resolvingPersisted = Boolean((props.preferPersistedMatch || props.awaitingPersistedMatch) && !apiResult && !created);
    const matchState: RequestorMatchState = hasKeys || apiResult
        ? mapRequestorMatchState(apiResult, { hasKeys, searched: Boolean(apiResult), isSearching, serviceUnavailable, userEntered: Boolean(created) }, {
            name: props.extractedName,
            address1: props.extractedAddress1,
            city: props.extractedCity,
        })
        : DEFAULT_REQUESTOR_MATCH_STATE;

    // Report the computed match state up so the parent's badge reflects the
    // REAL result. Keyed on the primitive status/confidence (not the freshly-
    // built matchState object) so a same-status re-render doesn't re-fire this
    // effect and cause a report/setState render loop.
    const { onMatchStateChange } = props;
    useEffect(() => {
        // During the reopen resolve window report a transient "matching" state so
        // the badge doesn't flash NeedsEntry off the suppressed keys (AB#31605).
        onMatchStateChange?.(resolvingPersisted ? { ...DEFAULT_REQUESTOR_MATCH_STATE, status: "matching" } : matchState);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [resolvingPersisted, matchState.status, matchState.confidence, onMatchStateChange]);

    // AC9 (coalesce 2026-07-20 fix): mirror PatientInfoRow - when the row resolves
    // to a confident single EXISTING requestor (automated green match or grid
    // pick, both in apiResult as matched/Single), write its identity into the
    // form so it survives unmount and reaches the committed transaction. Only
    // org/address1/city are available from the single-match payload today (the
    // contact/fax columns are a separate open proc gap). Create-new excluded
    // (`created`). Write-once per identity (requestorId -> fullName) via a ref.
    const { onSelectRequestor } = props;
    const lastWrittenRequestorKeyRef = useRef<string | undefined>(undefined);
    useEffect(() => {
        if (created) {
            return; // create-new path owns the form write
        }
        if (!apiResult?.matched || matchState.confidence !== "single") {
            return;
        }
        // Key on requestor + contact so resolving a DIFFERENT contact of the
        // same requestor still writes (Spec #31605 follow-on: candidates are
        // per-(requestor, contact) rows).
        const key = `${apiResult.requestorId ?? apiResult.fullName ?? ""}|${apiResult.contactId ?? ""}`;
        if (key === "|" || lastWrittenRequestorKeyRef.current === key) {
            return;
        }
        lastWrittenRequestorKeyRef.current = key;
        // Full org address + the resolved contact now come from the QuickEntry
        // search payload (ROIS_RequestorSearchQuickEntry_sel). Previously state/
        // zip/contact were written blank, producing an incomplete send-to address
        // and blanking any prior values (AB#31605 / Feature 31605 follow-on).
        onSelectRequestor?.({
            organization: apiResult.fullName ?? "",
            address1: apiResult.address1 ?? "",
            address2: apiResult.address2 ?? "",
            city: apiResult.city ?? "",
            state: apiResult.state ?? "",
            zip: apiResult.zipCode ?? "",
            contactFirstName: apiResult.contactFirstName ?? "",
            contactLastName: apiResult.contactLastName ?? "",
            email: apiResult.email ?? "",
            phone: apiResult.phone ?? "",
            fax: apiResult.fax ?? "",
        // Carry the resolved requestor + contact DB ids so the parent can link them
        // to the transaction (sendToRequestorid / sendToRequestorContactid) at
        // Confirm All. Both live directly on apiResult for the auto match and the
        // grid pick (onModalSelect spreads the chosen candidate, which carries both).
        // AB#35289: also carry how this resolved ("auto" | "manual") so the parent can
        // re-evaluate an auto match on a type->Internal flip but preserve deliberate picks.
        //
        // AB#35253 - THIS PATH IS NOT VALIDATED, deliberately, and it is the one door the City/State
        // /Zip patterns below do not reach. No form opens on an automated single match, so the
        // MATCHED RECORD's stored city/state/zip go into the transaction and the send-to mirror as
        // they are: a stored "Rochester3" still reaches persistence here. The patterns are field
        // rules and cover the two doors a form has - a typed value and a pre-filled one.
        //
        // Not gated here, on purpose. Refusing to write would blank the send-to address that
        // AB#31605 exists to populate, and the specialist has no form open in which to correct the
        // value - so a gate would strand the transaction rather than fix the data. Values from the
        // extraction and from the specialist are validated; a record already in the database is a
        // data-cleanup question, raised as a follow-up rather than settled here.
        }, apiResult.requestorId, apiResult.contactId, apiResultSourceRef.current);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [apiResult, matchState.confidence, created, onSelectRequestor]);

    // The inline Search button opens the classic requestor modal (RightPane
    // activeTabId=2), which searches via the SAME classic stored proc as classic
    // mode — ROIS_RequestorSearch_sel_v2 — so QE results match classic exactly.
    // (Randy 2026-07-24: the regular Search button pops the requestor modal; no
    // separate "requestor search / assign contact" button.)
    //
    // The TYPED values go with it. They used to be dropped and the modal re-seeded
    // itself from the AI-extracted fax/email, so clearing the fax and entering an
    // email still searched on the extracted fax - the specialist's edits never
    // reached the API. Passed verbatim, including empty strings: a cleared box means
    // "don't search on this", not "fall back to the document".
    // Classic's Contact box is ONE field and RequestorManager.FindRequestors splits it on the
    // first space, so joining the extracted parts round-trips: "Dana Smith" -> First=Dana,
    // Last=Smith. A single token becomes First=Last, which the proc turns into an OR.
    // Review: trim before joining, not just before checking emptiness - a trailing space on the
    // first name ("Dana " + "Smith") survives the old filter-then-join into "Dana  Smith", and
    // RequestorManager.FindRequestors' Split(' ', 2) turns that into LastName=" Smith", which
    // misses a stored "Smith". A leading space is worse: it drops FirstName entirely.
    const extractedContactName = [props.extractedContactFirstName, props.extractedContactLastName]
        .map(part => (part ?? "").trim())
        .filter(part => part !== "")
        .join(" ") || undefined;

    const onInlineSearch = (values: Readonly<Record<string, string>>) => {
        const fax = values.fax ?? "";
        const email = values.email ?? "";
        const address = values.address ?? "";
        // AB#39478 review: the search proc prefix-matches ZipCode against this value
        // (`LIKE @ZipCode + '%'`), so a ZIP+4 extracted value ("14624-1234") never
        // matches a stored plain 5-digit zip. Truncate to the 5-digit zip for search.
        const zip = (values.zip ?? "").split("-")[0];
        const requestorName = values.requestorName ?? "";
        const contactName = values.contactName ?? "";

        // AB#39478 review: matches Classic exactly - InlineSearchForm omits the advanced keys from
        // `values` entirely while its section is collapsed, so a pre-filled-but-hidden value can
        // never reach here. advancedFilled is therefore 0 whenever the section is closed, and Fax
        // or Email alone is a valid search either way. Classic's two-value minimum only bites when
        // the section is open and the specialist is relying on the advanced fields alone.
        const advancedFilled = [address, zip, requestorName, contactName].filter(v => v.trim()).length;
        if (!fax && !email) {
            if (advancedFilled === 0) {
                setSearchError(BASIC_SEARCH_MIN_MESSAGE);
                return;
            }
            if (advancedFilled < 2) {
                setSearchError(ADVANCED_SEARCH_MIN_MESSAGE);
                return;
            }
        }

        setSearchError("");
        // Everything populated, per the dev's call: the proc ANDs its criteria, so a pre-filled
        // Fax narrows a typed Address. The boxes are editable and stay cleared, so that is the
        // specialist's lever.
        props.onOpenRequestorModal?.({ fax, email, address, zip, requestorName, contactName });
    };

    // Spec #35136 (F1f) fix: Classic's own DDS search isn't site-code-only - it
    // bundles Site Code, Fax, and Email (and more) into one call. The DDS inline
    // search box offers all three as typed overrides, pre-filled with the row's
    // current values (currentDdsSiteCode/extractedFax/extractedEmail below), so
    // an untouched box still searches with the current data rather than blanking
    // it - same effect as "leave it alone", just via pre-fill instead of undefined.
    const onInlineDdsSearch = (values: Readonly<Record<string, string>>) => {
        props.onOpenRequestorModal?.({
            siteCode: values.siteCode,
            fax: values.fax,
            email: values.email,
        });
    };

    const onModalSelect = async (rowId: string | number) => {
        // AB#39052: resolved through requestorCandidateKey, the SAME namespaced key the cards
        // and the grid render with - a bare `contactId ?? requestorId` comparison here would
        // take the first candidate whose id matched in EITHER domain.
        const selected = apiResult?.candidates?.find(candidate => requestorCandidateKey(candidate) === rowId);
        setModalOpen(false);
        if (!selected) {
            return;
        }
        // AB#35087: remember how many were returned when the specialist tie-broke
        // a MULTIPLE result (captured BEFORE we flatten apiResult to the single
        // pick) so the info tooltip reads "N returned — you selected this one"
        // instead of a false "one returned, no tie-break needed".
        const returnedCount = apiResult?.candidates?.length ?? apiResult?.matchCount ?? 0;
        setSelectedFromCount(returnedCount > 1 ? returnedCount : undefined);
        // AB#39052: the candidate carries the contact's ID and no demographics, so they are
        // read here - once, for the one candidate the specialist confirmed.
        const resolved = await withResolvedContact({
            ...selected, matched: true, matchMethod: apiResult?.matchMethod ?? "None", matchCount: 1, confidence: "Single",
        });
        if (!isMountedRef.current) {
            return;
        }
        // AB#35289: an explicit inline-grid pick is a deliberate operator choice -> "manual".
        applyApiResult(resolved, "manual");
    };

    // Create New (AC9 no-match path): open the create-new requestor form,
    // pre-filled with the AI-extracted values. Closes the results grid if open.
    /**
     * AB#38824: opens the create form, deciding ONCE whether it carries the document's values.
     *
     * Latched rather than read live, because the modal seeds its inputs in a lazy initializer and
     * never re-reads `fields` - the VALUES are frozen at mount whatever happens afterwards.
     * Reading the rule live would let a form opened in `awaitingDocument` have its wording change
     * the instant the extraction landed and the automated match resolved, while its boxes stayed
     * exactly as they were.
     *
     * Latched ONLY on a genuine closed -> open transition. When the form is already open,
     * `setCreateOpen(true)` is a no-op and the modal is NOT remounted, so re-latching would move
     * the divergence rather than close it: new wording over old, frozen inputs.
     */
    const openCreateForm = () => {
        if (!createOpen) {
            setCreatePrefills(shouldPrefillCreateForm(matchState));
        }
        setCreateOpen(true);
    };

    const onOpenCreate = () => {
        setModalOpen(false);
        openCreateForm();
    };

    // The create-new form fields mirror the classic add-new-requestor form's
    // org+address block. The CONTACT block deliberately lives elsewhere: a requestor's
    // contact is chosen (or created) through Assign Contact on the resolved row, which
    // is also the only way to attach one of the requestor's EXISTING contacts. Keeping a
    // contact block here would offer a second, create-only path to the same thing.
    // Field caps + placeholders mirror the classic add-new-requestor form
    // (DataEntryDetailsRightPane / RequestorFormConstants).
    // AB#38824: the org/address block carries the extraction forward ONLY when no DB match was
    // found. With a match on the row, the specialist choosing "add new" is deliberately entering
    // a requestor the database does not hold, so the form opens EMPTY rather than making them
    // clear six boxes of a different organisation's details first. The rule and every status it
    // turns on live in createPrefill.ts.
    //
    // Gating the VALUES rather than the buttons: this row declares two ways in and only one of
    // them is live. `createOpenSignal` - bumped when Classic's own results grid gets its "Add new
    // Requestor" click (AB#35116) - is the reachable one, and it is the door QA walked through.
    // The other, MatchGridModal's "+ Create New Requestor", is currently dead: `modalOpen` is set
    // only under `if (opts?.manual)` in runSearch, and neither of this row's two runSearch calls
    // passes it. Gating the array covers the live door, the dead one if it is ever revived, and
    // any third that gets added - none of them can reintroduce the bug by forgetting a guard.
    const prefill = (value: string | undefined) => (createPrefills ? value : undefined);

    const createFields: readonly CreateEntityField[] = [
        { name: "organization", label: "Organization", value: prefill(props.extractedName), required: true, fullWidth: true, maxLength: 100 },
        { name: "address1", label: "Address", value: prefill(props.extractedAddress1), required: true, fullWidth: true, maxLength: 70 },
        { name: "address2", label: "Address 2", value: prefill(props.extractedAddress2), fullWidth: true, placeholder: "P.O. Box, Suite, Apt #", maxLength: 70 },
        // AB#35253 (QA retest 2026-08-17, "the issue still persists for the City field"): City
        // rejects digits and special characters, keeping letters plus the punctuation real place
        // names carry - knowingly STRICTER than Classic, which restricts City by no character class
        // at all (see the CITY note in quickEntryFieldConstraints). The cap stays Classic's 30.
        //
        // A `pattern` ONLY, no keystroke transform, so the typed value and the PRE-FILLED one (the
        // extraction on create, the requestor record on edit) are judged by the same predicate. A
        // pre-filled value never passes through onChange, so a keystroke rule could not have covered
        // the reported defect at all - it was the form's DEFAULT state, with Save enabled.
        //
        // Consequence, stated: a stored city carrying digits blocks an address-only edit until the
        // specialist corrects it. That is what "City must reject numerics" means on the edit path.
        {
            name: "city",
            label: "City",
            value: prefill(props.extractedCity),
            required: true,
            maxLength: QUICK_ENTRY_FIELD_MAX_LENGTH.requestorCity,
            pattern: QUICK_ENTRY_CITY_REGEX,
            invalidMessage: INVALID_CITY_MESSAGE,
        },
        // AB#35253: State is a DROPDOWN of US abbreviations, matching Classic's requestor State
        // (ReduxFormDropdownField over UNITED_STATES, RequestorContactPanel.tsx ~543-558) and the
        // Bill To State field Quick Entry already renders that way (QuickEntryBillingPane.tsx
        // ~925). That is the root fix for what the specialist TYPES: a dropdown cannot emit a digit.
        //
        // The `pattern` covers the pre-fill door. An unlisted pre-fill is deliberately KEPT and
        // offered as an option so a requestor stored with a real code the list lacks stays editable;
        // unconditionally, that also kept an extracted "1L!" - selectable, pre-selected, no error,
        // Save enabled - which is the reported defect over again. Letters, 2 or 3 of them: it
        // refuses malformed values while still admitting an unlisted real code, bounded by the
        // varchar(3) column width that base carried as maxLength: 3 and a `select` cannot carry.
        {
            name: "state",
            label: "State",
            value: prefill(props.extractedState),
            required: true,
            type: "select",
            options: UNITED_STATES.map(st => st.abbreviation),
            pattern: QUICK_ENTRY_STATE_REGEX,
            invalidMessage: INVALID_STATE_MESSAGE,
        },
        // AB#37973 (David Heifetz, PR !9947 thread 53170): this is the zip field the bug actually
        // reports, and it was still MASKED with the postalCode kind - sanitizeZipInput strips every
        // non-digit per keystroke, so a Canadian "K1A 0B1" became "101" silently. That is the exact
        // destructive behaviour reverted for Bill To Zip in commit 638ea0b, for the exact same
        // reason, and it survived here only because the first fix went to the wrong field. Same
        // treatment now: cap only (no mask), plus Classic's own post-hoc zip validator, which tells
        // the specialist the value is wrong instead of rewriting it. No billingTypeId gate - unlike
        // Bill To, the requestor zip is unconditionally required.
        //
        // AB#35253 issue 2 (QA retest 2026-08-17): the CAP is Classic's entry-zip 5, not the
        // varchar(10) column width - QA measured Classic at 5 and Quick Entry at 10. The mask stays
        // gone; only the cap moved. See CLASSIC_ENTRY_ZIP_MAX_LENGTH for the ZIP+4 consequence.
        {
            name: "zip",
            label: "Zip",
            value: prefill(props.extractedZip),
            required: true,
            maxLength: CLASSIC_ENTRY_ZIP_MAX_LENGTH,
            pattern: CLASSIC_ZIP_REGEX,
            invalidMessage: INVALID_ZIP_MESSAGE,
        },
    ];

    /**
     * AB#38824: whether the form the specialist is looking at actually carries anything from the
     * document. Read off the FIELDS, not off the rule, so the wording cannot describe a form it is
     * not attached to. Two cases where the rule alone would lie: `awaitingDocument` pre-fills
     * "vacuously" - permitted, but there is no extraction, so every box is empty and "review the
     * details captured from the document" is nonsense; and a partial extraction is still a
     * pre-fill worth reviewing.
     */
    const carriesDocumentValues = createFields.some(f => (f.value ?? "").trim() !== "");


    /**
     * The org+address form no longer carries the contact block (it moved to Assign
     * Contact), so `keepContact` supplies the contact fields the caller must not lose.
     * Create passes nothing - a new requestor starts contactless and the specialist
     * assigns one. EDIT passes the requestor already on the row, otherwise saving an
     * address correction would blank an assigned contact out of both the row and the
     * form (and with it the SEND_TO_* mirrors).
     */
    const toCreateValues = (
        values: Readonly<Record<string, string>>,
        keepContact?: Pick<RequestorCreateValues, "contactFirstName" | "contactLastName" | "email" | "phone" | "fax">,
    ): RequestorCreateValues => ({
        organization: values.organization ?? "",
        address1: values.address1 ?? "",
        address2: values.address2 ?? "",
        city: values.city ?? "",
        state: values.state ?? "",
        zip: values.zip ?? "",
        contactFirstName: values.contactFirstName ?? keepContact?.contactFirstName ?? "",
        contactLastName: values.contactLastName ?? keepContact?.contactLastName ?? "",
        email: values.email ?? keepContact?.email ?? "",
        phone: values.phone ?? keepContact?.phone ?? "",
        fax: values.fax ?? keepContact?.fax ?? "",
    });

    /**
     * An explicitly assigned contact replaces the contact block the requestor record or
     * the document supplied - it IS the contact the transaction will carry, so the row
     * must show it rather than the value it superseded.
     */
    const withAssignedContact = <T extends Pick<RequestorCreateValues, "contactFirstName" | "contactLastName" | "email" | "phone" | "fax">>(source: T): T => (
        props.assignedContact ? { ...source, ...props.assignedContact } : source
    );

    // An assigned contact wins over the record's, so the Contact section of the edit
    // form opens on the contact the row is actually showing.
    const editingBase: RequestorCreateValues | undefined = created ?? selected ?? (apiResult?.matched ? {
        organization: apiResult.fullName ?? "",
        address1: apiResult.address1 ?? "",
        address2: apiResult.address2 ?? "",
        city: apiResult.city ?? "",
        state: apiResult.state ?? "",
        zip: apiResult.zipCode ?? "",
        contactFirstName: apiResult.contactFirstName ?? "",
        contactLastName: apiResult.contactLastName ?? "",
        email: apiResult.email ?? "",
        phone: apiResult.phone ?? "",
        fax: apiResult.fax ?? "",
    } : undefined);
    const editing: RequestorCreateValues | undefined = editingBase ? withAssignedContact(editingBase) : undefined;

    /**
     * "This requestor has a contact" - any contact value present. Deliberately the same
     * shape as the parent's willLinkContact (the amber gate's predicate), so the gate,
     * the Assign/Change Contact label and the edit form's Contact section can't disagree
     * about whether a contact exists.
     */
    /*
     * AB#39052: a pinned contact ID IS a contact, whether or not its demographics have
     * arrived - the search returns the id alone now, and `resolveContact` fills the rest in
     * a moment later (or not at all, if it fails). The parent's gate already reads it that
     * way - isRequestorAmber is `res.contactId == null && !willLinkContact(values)` - so
     * keying this only on the demographics would make the row nag for an assignment the gate
     * considers already made, which is the disagreement the comment above forbids.
     */
    const hasContact = apiResult?.contactId != null || Boolean(
        editing?.contactFirstName || editing?.contactLastName || editing?.email || editing?.phone || editing?.fax,
    );


    const onCreateSubmit = (values: Readonly<Record<string, string>>) => {
        const entered: RequestorCreateValues = toCreateValues(values);
        // Sticky: once a match has been declined on this row, every later create on it is still a
        // create against a declined match, whatever the synthesized state now says.
        const hadDbMatch = hasDbMatch(matchState) || declinedDbMatchRef.current;
        declinedDbMatchRef.current = hadDbMatch;
        props.onCreateRequestor?.(entered, hadDbMatch);
        // Resolve the row to the newly-entered requestor. `created` drives the
        // rich display (name + address/contact + source pills) and the blue
        // user-entered badge (userEntered signal). No DB write here - the
        // transaction's final Save persists the values.
        // A prior search attempt may have left serviceUnavailable=true (only
        // runSearch clears it) - deriveStatus checks serviceUnavailable before
        // anything else, so without this the row stays stuck showing the
        // search/create prompt instead of the just-created requestor.
        setServiceUnavailable(false);
        // The row is resolved now, so drop the inline search box. Without this, a create
        // started from an open search box (Reset, or the no-match path) saved fine but
        // the row kept showing the SEARCH INPUT instead of the new requestor - searchMode
        // is sticky and outranks the resolved display (Randy 2026-07-24).
        setSearchMode(false);
        setCreated(entered);
        // AB#35289: a create-new is deliberate -> "manual" (the onSelectRequestor effect is
        // skipped while `created` is set, so this is belt-and-suspenders for the invariant).
        applyApiResult({
            matched: true,
            fullName: entered.organization,
            address1: entered.address1,
            city: entered.city,
            eTransfer: false,
            national: false,
            transactionCount: 0,
            matchMethod: apiResult?.matchMethod ?? "None",
            matchCount: 1,
            confidence: "Single",
        }, "manual");
        setCreateOpen(false);
    };

    // AB#35116: a pick in the classic results grid resolves this row. Rendered as the
    // rich detail block (org + address + contact, FROM DATABASE pills) and pushed into
    // apiResult as a matched/Single result, so the badge, the amber gate and the
    // onSelectRequestor form/send-to writes all run through the paths they already use
    // for an automated single match - no second resolution mechanism.
    const { selectedRequestor } = props;
    useEffect(() => {
        if (!selectedRequestor) {
            return;
        }
        setCreated(undefined);
        setSearchMode(false);
        setSelected(selectedRequestor);
        // AB#35289 (round-5 CRITICAL): a classic results-grid pick is a DELIBERATE operator choice
        // -> "manual". Tagging it here (not relying on a later onModalRequestorContactResolved to
        // promote it) is load-bearing: a pick with no contact assigned yet (amber) then a flip to
        // Internal would otherwise report "auto" and be wiped by resetAutoRequestorForInternal.
        applyApiResult({
            matched: true,
            requestorId: selectedRequestor.requestorId,
            fullName: selectedRequestor.organization,
            address1: selectedRequestor.address1,
            address2: selectedRequestor.address2,
            city: selectedRequestor.city,
            state: selectedRequestor.state,
            zipCode: selectedRequestor.zip,
            eTransfer: Boolean(selectedRequestor.eTransfer),
            national: Boolean(selectedRequestor.national),
            transactionCount: 0, // the classic search proc doesn't return it
            contactId: selectedRequestor.contactId,
            contactFirstName: selectedRequestor.contactFirstName,
            contactLastName: selectedRequestor.contactLastName,
            email: selectedRequestor.email,
            phone: selectedRequestor.phone,
            fax: selectedRequestor.fax,
            matchMethod: "None",
            matchCount: 1,
            confidence: "Single",
        }, "manual");
    }, [selectedRequestor]);

    // AB#35116: "Add new Requestor" in the classic results grid opens this row's
    // create-requestor modal (prefilled from the document, same as the AC9 no-match
    // path). Keyed on the counter so a second click reopens it.
    //
    // AB#37983: the counter is an EVENT ("the specialist just clicked Add New Requestor"), not a
    // piece of state, and `if (props.createOpenSignal)` replayed it on every MOUNT. That is what
    // put the popup on the next transaction: advancing bumps quickEntryIntakeGeneration, which
    // remounts this row, and the effect then fired against a counter still carrying the PREVIOUS
    // transaction's click. The parent never resets the counter, so it stays non-zero for the rest
    // of the session.
    //
    // Fixed here rather than by clearing the counter in the parent's reset blocks, because a
    // remount is not unique to advancing: a document replace bumps the same generation, and an
    // Internal type switch bumps quickEntryRequestorResetGeneration, which remounts THIS ROW on
    // its own key. Resetting per-path means finding every trigger and remembering the next one;
    // latching the value seen at mount closes all of them at the source. Comparing against the
    // latch rather than truthiness also means a genuine second click still reopens the modal,
    // which is the behaviour AB#35116 added.
    const seenCreateSignalRef = useRef(props.createOpenSignal);
    useEffect(() => {
        if (props.createOpenSignal !== seenCreateSignalRef.current) {
            seenCreateSignalRef.current = props.createOpenSignal;
            openCreateForm();
        }
        // openCreateForm reads this render's `matchState` and `createOpen`. React keeps the latest
        // effect closure and only RUNS it when the counter changes, so the read is current, not
        // stale - but the rule cannot see that. Disabled explicitly, as every other justified
        // deviation in this file is (John James, PR !10371).
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.createOpenSignal]);

    // "Search manually" (Multiple state): open the inline search over the grid so
    // the specialist can re-run with typed keys instead of picking a candidate.
    // Same for every row, DDS included - showInlineSearch's DDS branch (below)
    // renders the fields-less variant instead of Fax/Email, so this doesn't need
    // to special-case DDS itself.
    const onSearchManually = () => {
        setModalOpen(false);
        setSearchMode(true);
    };

    // Cancel/revert: dismiss the inline search WITHOUT running a search. apiResult
    // (and its candidates) is never cleared, so we drop back to the prior display -
    // the matched-single value, or, for a Multiple result, the candidate grid with
    // the set intact (no auto-pick - AC6 / Business Rule 5).
    const onInlineCancel = () => {
        // Just close the inline search - the row falls back to its prior display:
        // the matched-single value, or the Multiple inline card list (re-rendered
        // automatically once searchMode is false; the candidate set is preserved).
        setSearchMode(false);
        setSearchError("");   // AB#39478: a dismissed form must not leave its refusal behind
    };

    // Requestor Search grid: ORGANIZATION (sortable, with the e-transfer/national badges
    // inline) | ADDRESS | PRIOR. Each row still keys on the candidate's contact id where it
    // has one, so a pick resolves that contact. (Classic requestor search is unchanged; it
    // still uses ROIS_RequestorSearch_sel_v2 and its own grid.)
    //
    // AB#39052 (Ali Moll, PR !10513 thread 56655): CONTACT and FAX are gone and ADDRESS
    // takes their place. The search no longer returns contact demographics, and they were
    // the wrong columns for this grid anyway - every row in it matched the SAME fax or
    // email, so both columns printed the same value down the list and separated nothing.
    // Worse, FAX could print a value that was not the match: the filter is
    // "rc.Phone LIKE @PhoneFax OR rc.Fax LIKE @PhoneFax", so a contact reached the grid on
    // its phone while the column showed a different or empty fax. The address is the field
    // that actually differs - the same reason the inline cards lead with it.
    const gridColumns: readonly MatchGridColumn[] = [
        { id: "organization", label: "ORGANIZATION", sortable: true },
        { id: "address", label: "ADDRESS" },
        { id: "prior", label: "PRIOR" },
    ];

    const gridRows: readonly MatchGridRow[] = (apiResult?.candidates ?? []).map(candidate => ({
        // AB#39052: the same namespaced identity the inline cards use, because both feed
        // the one onModalSelect lookup - see requestorCandidateKey.
        id: requestorCandidateKey(candidate),
        values: {
            organization: candidate.fullName ?? "",
            address: candidateAddressLine(candidate),
            prior: String(candidate.transactionCount ?? 0),
        },
        adornments: {
            organization: (
                <>
                    {candidate.eTransfer ? (
                        <span className="requestor-info-row__badge requestor-info-row__badge--etransfer" role="img" title="e-transfer" aria-label="e-transfer"> ⚡</span>
                    ) : null}
                    {candidate.national ? (
                        <span className="requestor-info-row__badge requestor-info-row__badge--national" role="img" title="national" aria-label="national"> 🌐</span>
                    ) : null}
                </>
            ),
        },
    }));

    const hasCandidates = gridRows.length > 0;

    // Inline search-entry is shown when there's no confident single match to
    // display, or when re-searching a matched row via [Search]. Multiple
    // candidates resolve through the results modal instead.
    // awaitingDocument (no extracted keys / intake not loaded) also opens the
    // inline search so the requestor can always be entered/searched manually,
    // rather than sitting as a dead placeholder (coalesce 2026-07-20).
    const showInlineSearch =
        matchState.status === "awaitingDocument" || matchState.status === "noMatch" || matchState.status === "serviceUnavailable" || searchMode || manualSearchPending;

    // Source provenance pill: a created field prefilled from the extracted
    // document reads FROM DOC; one the specialist typed reads MANUAL.
    const renderSourcePill = (source?: "doc" | "manual" | "database") => {
        if (!source) {
            return null;
        }
        const label = source === "doc" ? "FROM DOC" : source === "database" ? "FROM DATABASE" : "MANUAL";
        return <span className={`requestor-info-row__source-pill requestor-info-row__source-pill--${source}`}>{label}</span>;
    };

    const sourceOf = (value: string, extracted?: string): "doc" | "manual" | undefined =>
        value ? (extracted && value === extracted ? "doc" : "manual") : undefined;

    // AB#35066: the ≡ grounding icon, shown ONLY for a FROM DOC value (matched-
    // requestor fields are all FROM DATABASE and get none). Rendered just before
    // the source pill; wired to the parent's reveal/no-source handlers.
    const renderGroundingForSource = (source: "doc" | "manual" | "database" | undefined, field: string, label: string) => {
        if (source !== "doc") {
            return null;
        }
        return (
            <QuickEntryGroundingIcon
                field={field}
                label={label}
                fieldGroundings={props.fieldGroundings}
                onReveal={props.onRevealGrounding}
                onUnavailable={props.onGroundingUnavailable}
            />
        );
    };

    /**
     * Assign Contact affordance, shown on every RESOLVED requestor display (created,
     * picked, auto-matched) so the contact step is reachable from the row instead of
     * only from inside the classic requestor panel. Until a contact is assigned the row
     * is amber (REQUESTOR_CONTACT_REQUIRED_MESSAGE) and Confirm All is blocked - this is
     * the way out of that gate.
     */
    const renderAssignContact = (hasContact: boolean) => {
        if (!props.onAssignContact) {
            return null;
        }
        return (
            <div className="requestor-info-row__assign-contact">
                <button
                    type="button"
                    className="requestor-info-row__assign-contact-btn"
                    onClick={() => props.onAssignContact?.()}
                >
                    {hasContact ? "Change Contact" : "Assign Contact"}
                </button>
            </div>
        );
    };

    // Rich created-requestor display (master-demo-v6): organization + a "New
    // requestor" note (blue) + address/contact rows with source pills.
    // `origin: "selected"` reuses the same block for a requestor picked from the classic
    // results grid (AB#35116) - existing record, so every field reads FROM DATABASE.
    const renderCreatedRequestor = (base: RequestorCreateValues, origin: "created" | "selected" = "created") => {
        const c = withAssignedContact(base);
        // AB#35087: the discriminator is props.selectedRequestor, NOT matchMethod - a classic pick
        // sets matchMethod to "None", but so does an automatic AI-extraction match, so gating on it
        // mislabelled those as search-selected. AB#38736 removed the second writer of `selected`
        // (the Edit form), leaving the classic grid pick as the only one.
        const pickedFromSearch = Boolean(props.selectedRequestor);
        // Only claim a match story when a match genuinely resolved; otherwise buildRequestorMatchTooltip
        // would assert a search that never ran (its "None" wording is "Searched by AI extraction").
        const showMatchStory = !pickedFromSearch && matchState.status === "matched";
        const selectedRequestorTooltip = showMatchStory
            ? buildRequestorMatchTooltip({
                fax: props.extractedFax ?? apiResult?.fax,
                email: props.extractedEmail ?? apiResult?.email,
                matchMethod: matchState.matchMethod,
                matchCount: matchState.matchCount,
                selectedFromCount,
            })
            : buildSelectedRequestorTooltip();
        const contact = [c.contactFirstName, c.contactLastName].filter(Boolean).join(" ");
        const cityStateZip = [c.city, [c.state, c.zip].filter(Boolean).join(" ")].filter(Boolean).join(", ");
        const fromDb = origin === "selected";
        // One effective-source helper feeding BOTH the grounding icon and the pill: a
        // requestor picked from the classic grid is an existing record (FROM DATABASE),
        // so its fields never claim a document source - which also means no grounding
        // icon, since renderGroundingForSource only renders for "doc".
        // AB#38736: the per-VALUE "manual" case is gone with the Edit form - nothing can overtype a
        // resolved field any more, so every value on this panel really is database-sourced.
        const sourceFor = (value: string, extracted: string | undefined): "doc" | "manual" | "database" | undefined =>
            fromDb ? (value ? "database" : undefined) : sourceOf(value, extracted);
        return (
            <div className="requestor-info-row__detail">
                <div className="requestor-info-row__name">{c.organization}</div>
                <div className="requestor-info-row__match-note requestor-info-row__match-note--new">
                    <span className="requestor-info-row__new-icon" aria-hidden="true" /> {fromDb ? "Selected requestor" : "New requestor"}
                    {/* AB#35087: this path short-circuits renderValue before renderMatchedRequestor,
                        so it never had an info icon - which is what QA saw as "missing entirely".
                        Content is chosen above by provenance: a genuine manual pick gets the
                        "selected" copy (no match ran, so the match narrative and the ordered rules
                        would both be false), an edited auto-match keeps its real match story.
                        Only for the SELECTED case - a freshly created requestor has no selection
                        story to explain, so "New requestor" keeps no icon. */}
                    {fromDb ? (
                        <MatchInfoTooltip title={<MatchTooltipBody content={selectedRequestorTooltip} />} placement="top">
                            <span className="requestor-info-row__info-icon" tabIndex={0} role="img" aria-label={flattenMatchTooltip(selectedRequestorTooltip)}>&#9432;</span>
                        </MatchInfoTooltip>
                    ) : null}
                </div>
                <dl className="requestor-info-row__fields">
                    {c.address1 ? (
                        <div className="requestor-info-row__field">
                            <dt>Address</dt>
                            <dd>{[c.address1, c.address2].filter(Boolean).join(", ")}{renderGroundingForSource(sourceFor(c.address1, props.extractedAddress1), "requestorAddress1", "Requestor Address")}{renderSourcePill(sourceFor(c.address1, props.extractedAddress1))}</dd>
                        </div>
                    ) : null}
                    {cityStateZip ? (
                        <div className="requestor-info-row__field">
                            <dt>City/State/Zip</dt>
                            <dd>{cityStateZip}{renderGroundingForSource(sourceFor(c.city, props.extractedCity), "requestorCity", "Requestor City")}{renderSourcePill(sourceFor(c.city, props.extractedCity))}</dd>
                        </div>
                    ) : null}
                    {contact ? (
                        <div className="requestor-info-row__field">
                            <dt>Contact</dt>
                            <dd>{contact}{renderGroundingForSource(sourceFor(c.contactFirstName, props.extractedContactFirstName), "requestorContactGivenName", "Requestor Contact")}{renderSourcePill(sourceFor(c.contactFirstName, props.extractedContactFirstName))}</dd>
                        </div>
                    ) : null}
                    {c.fax ? (
                        <div className="requestor-info-row__field">
                            <dt>Fax</dt>
                            <dd>{c.fax}{renderGroundingForSource(sourceFor(c.fax, props.extractedFax), "requestorContactFax", "Requestor Fax")}{renderSourcePill(sourceFor(c.fax, props.extractedFax))}</dd>
                        </div>
                    ) : null}
                    {c.phone ? (
                        <div className="requestor-info-row__field">
                            <dt>Phone</dt>
                            <dd>{c.phone}{renderGroundingForSource(sourceFor(c.phone, props.extractedPhone), "requestorContactPhone", "Requestor Phone")}{renderSourcePill(sourceFor(c.phone, props.extractedPhone))}</dd>
                        </div>
                    ) : null}
                    {c.email ? (
                        <div className="requestor-info-row__field">
                            <dt>Email</dt>
                            <dd>{c.email}{renderGroundingForSource(sourceFor(c.email, props.extractedEmail), "requestorContactEmail", "Requestor Email")}{renderSourcePill(sourceFor(c.email, props.extractedEmail))}</dd>
                        </div>
                    ) : null}
                </dl>
            </div>
        );
    };

    // Rich matched-requestor display for a confident SINGLE (green) match.
    // AB#35081: previously a selected/auto-matched existing requestor fell through to
    // the plain confidence span below, showing only org name + e-transfer badge +
    // prior count + "differs" labels - NOT the Address/City/State/Zip/Contact/Fax/
    // Email the payload carries (and that a newly-CREATED requestor already shows via
    // renderCreatedRequestor). This mirrors PatientInfoRow.renderMatchedPatient so a
    // matched requestor shows its full detail set, especially for E-Transfer records.
    // AB#35087: also adds the "i" info icon (with the app's LightTooltip) the mockup
    // shows on the Requestor row - it was missing entirely here.
    const renderMatchedRequestor = () => {
        const r = apiResult;
        // An assigned contact supersedes the matched record's contact/fax/email.
        const rc = withAssignedContact({
            contactFirstName: r?.contactFirstName ?? "",
            contactLastName: r?.contactLastName ?? "",
            email: r?.email ?? "",
            phone: r?.phone ?? "",
            fax: r?.fax ?? "",
        });
        const contact = [rc.contactFirstName, rc.contactLastName].filter(Boolean).join(" ");
        const cityStateZip = [r?.city, [r?.state, r?.zipCode].filter(Boolean).join(" ")].filter(Boolean).join(", ");
        const methodLabel = matchState.matchMethod === "Fax" ? "fax" : matchState.matchMethod === "Email" ? "email" : "AI extraction";
        // AB#35087: rich "How this match was made" tooltip matching master-demo-v6 -
        // the search term + count + tie-break note PLUS the ordered requestor
        // match-selection rules. Fax/email shown are already on this card (on-screen
        // UI, not logged - see matchState.buildRequestorMatchTooltip).
        const requestorTooltip = buildRequestorMatchTooltip({
            fax: props.extractedFax ?? r?.fax,
            email: props.extractedEmail ?? r?.email,
            matchMethod: matchState.matchMethod,
            matchCount: matchState.matchCount,
            selectedFromCount,
        });
        // Matched-record fields come FROM DATABASE (the resolved requestor record),
        // same treatment as renderMatchedPatient's DB-sourced fields.
        return (
            <div className="requestor-info-row__detail">
                <div className="requestor-info-row__name">
                    {r?.fullName ?? props.extractedName}
                    {matchState.eTransfer ? <span className="requestor-info-row__etransfer" aria-label="E-transfer enabled">E-Transfer</span> : null}
                </div>
                <div className="requestor-info-row__match-note">
                    Matched on {methodLabel} — single result
                    <MatchInfoTooltip title={<MatchTooltipBody content={requestorTooltip} />} placement="top">
                        <span className="requestor-info-row__info-icon" tabIndex={0} role="img" aria-label={flattenMatchTooltip(requestorTooltip)}>&#9432;</span>
                    </MatchInfoTooltip>
                    <span className="requestor-info-row__transaction-count">{matchState.transactionCount} prior request{matchState.transactionCount === 1 ? "" : "s"}</span>
                </div>
                <dl className="requestor-info-row__fields">
                    {r?.address1 ? (
                        <div className="requestor-info-row__field">
                            <dt>Address</dt>
                            <dd>{[r.address1, r.address2].filter(Boolean).join(", ")}{renderSourcePill("database")}</dd>
                        </div>
                    ) : null}
                    {cityStateZip ? (
                        <div className="requestor-info-row__field">
                            <dt>City/State/Zip</dt>
                            <dd>{cityStateZip}{renderSourcePill("database")}</dd>
                        </div>
                    ) : null}
                    {contact ? (
                        <div className="requestor-info-row__field">
                            <dt>Contact</dt>
                            <dd>{contact}{renderSourcePill("database")}</dd>
                        </div>
                    ) : null}
                    {rc.fax ? (
                        <div className="requestor-info-row__field">
                            <dt>Fax</dt>
                            <dd>{rc.fax}{renderSourcePill("database")}</dd>
                        </div>
                    ) : null}
                    {rc.phone ? (
                        <div className="requestor-info-row__field">
                            <dt>Phone</dt>
                            <dd>{rc.phone}{renderSourcePill("database")}</dd>
                        </div>
                    ) : null}
                    {rc.email ? (
                        <div className="requestor-info-row__field">
                            <dt>Email</dt>
                            <dd>{rc.email}{renderSourcePill("database")}</dd>
                        </div>
                    ) : null}
                </dl>
                {matchState.differs.name || matchState.differs.address || matchState.differs.city ? (
                    <div className="requestor-info-row__differs-group">
                        {matchState.differs.name ? <span className="requestor-info-row__differs requestor-info-row__differs--name">Name differs from record</span> : null}
                        {matchState.differs.address ? <span className="requestor-info-row__differs requestor-info-row__differs--address">Address differs from record</span> : null}
                        {matchState.differs.city ? <span className="requestor-info-row__differs requestor-info-row__differs--city">City differs from record</span> : null}
                    </div>
                ) : null}
            </div>
        );
    };

    // Multiple (yellow): inline radio-card list (master-demo-v6) - top candidate
    // pre-selected (match rules: e-transfer, then highest prior volume), an
    // explanatory note above, "Confirm requestor" + "search manually" below.
    const renderMultipleRequestor = () => {
        const cands = apiResult?.candidates ?? [];
        // Candidates are per-(requestor, contact) rows, so their identity is contactId
        // (falling back to requestorId for a contactless requestor) - the SAME key
        // onModalSelect and the grid use. Keying these cards on requestorId alone made
        // Confirm silently no-op (onModalSelect looked up by contactId and found nothing)
        // and collided React keys when one requestor had multiple matching contacts.
        // AB#39052: candidates are now one per REQUESTOR (RequestorManager groups by RequestorID),
        // and a requestor that matched through several contacts carries no contactId at all - so
        // this keys on requestorId for exactly those. The contactId arm stays for the
        // single-contact case, where it is the contact the search actually found. The key is
        // NAMESPACED so those two arms cannot collide - see requestorCandidateKey.
        const candidateKey = requestorCandidateKey;
        const addressLine = candidateAddressLine;
        // No auto-pick: the user must explicitly choose a candidate. (Randy 2026-07-24:
        // cancelling/closing a search must NOT leave the first result selected.)
        const effectiveId = selectedCandidateId;
        const sharedKey = props.extractedFax ? `fax ${props.extractedFax}` : props.extractedEmail ? `email ${props.extractedEmail}` : "the same contact info";
        return (
            <div className="requestor-info-row__multiple">
                <div className="requestor-info-row__multiple-note">
                    {cands.length} requestors share {sharedKey}. Select the correct requestor.
                </div>
                <div className="requestor-info-row__cards">
                    {cands.map(c => {
                        const id = candidateKey(c);
                        const selected = id === effectiveId;
                        return (
                            <label key={id} className={`requestor-info-row__card${selected ? " requestor-info-row__card--selected" : ""}`}>
                                <input
                                    type="radio"
                                    name={`requestor-candidate-${REQUESTOR_INFO_ROW_ID}`}
                                    checked={selected}
                                    onChange={() => setSelectedCandidateId(id)}
                                />
                                <span className="requestor-info-row__card-body">
                                    <span className="requestor-info-row__card-name">
                                        {c.fullName}
                                        {c.eTransfer ? <span className="requestor-info-row__badge requestor-info-row__badge--etransfer"><span role="img" aria-label="e-transfer">⚡</span> E-Transfer</span> : null}
                                        {c.national ? <span className="requestor-info-row__badge requestor-info-row__badge--national"><span role="img" aria-label="national">🌐</span> National</span> : null}
                                    </span>
                                    {/* AB#39052: the address gets its own line, directly under the name -
                                        it is the only field that separates these candidates. The
                                        prior-request count follows on the line below.

                                        The Fax that used to sit in that meta line is gone with the
                                        rest of the contact demographics (Ali Moll, thread 56655): it
                                        printed the same value on every card, and not always the value
                                        that matched - a contact reaches this list on its phone too. */}
                                    {addressLine(c) ? <span className="requestor-info-row__card-address">{addressLine(c)}</span> : null}
                                    <span className="requestor-info-row__card-meta">
                                        <span>{c.transactionCount} prior request{c.transactionCount === 1 ? "" : "s"}</span>
                                    </span>
                                </span>
                            </label>
                        );
                    })}
                </div>
                <div className="requestor-info-row__multiple-actions">
                    <button type="button" className="btn requestor-info-row__confirm-btn" disabled={effectiveId == null} onClick={() => effectiveId != null && onModalSelect(effectiveId)}>
                        ✓ Confirm requestor
                    </button>
                    <button type="button" className="requestor-info-row__search-manually-link" onClick={onSearchManually}>
                        or search manually
                    </button>
                </div>
            </div>
        );
    };

    const renderValue = () => {
        // NOTE: awaitingDocument is intentionally absent - that status always
        // routes to the inline search (showInlineSearch), so renderValue is never
        // called for it. The old per-row "Awaiting document upload" placeholder was
        // deliberately dropped (coalesce 2026-07-20); the empty-intake message is now
        // the pane-level "Add a request document" panel in DataEntryDetails.
        switch (matchState.status) {
            case "matching":
                return <span className="requestor-info-row__matching">Matching...</span>;
            case "matched":
                if (created) {
                    return renderCreatedRequestor(created);
                }
                if (selected) {
                    return renderCreatedRequestor(selected, "selected");
                }
                if (matchState.confidence === "multiple" && hasCandidates) {
                    return renderMultipleRequestor();
                }
                if (matchState.confidence === "single") {
                    // AB#35081: rich matched-requestor detail (Address/City/Contact/Fax/Email),
                    // not just org name + badge + differs labels.
                    return renderMatchedRequestor();
                }
                // Fallback for any other matched confidence value (defensive - single/
                // multiple are the only reachable ones today).
                return (
                    <span className={`requestor-info-row__confidence requestor-info-row__confidence--${matchState.confidence}`}>
                        {apiResult?.fullName ?? props.extractedName}
                        {matchState.eTransfer ? <span className="requestor-info-row__etransfer" aria-label="E-transfer enabled">E-Transfer</span> : null}
                        <span className="requestor-info-row__transaction-count">{matchState.transactionCount} prior request{matchState.transactionCount === 1 ? "" : "s"}</span>
                        {matchState.differs.name ? <span className="requestor-info-row__differs requestor-info-row__differs--name"> Name differs from record</span> : null}
                        {matchState.differs.address ? <span className="requestor-info-row__differs requestor-info-row__differs--address"> Address differs from record</span> : null}
                        {matchState.differs.city ? <span className="requestor-info-row__differs requestor-info-row__differs--city"> City differs from record</span> : null}
                    </span>
                );
            default:
                return null;
        }
    };

    return (
        <div className="qe-row__body requestor-info-row" data-match-status={matchState.status}>
            {props.showLabel !== false ? <div className="qe-row__label">Requestor Info</div> : null}
            <div className="qe-row__value">
                {resolvingPersisted ? (
                    <span className="requestor-info-row__matching">Matching...</span>
                ) : showInlineSearch ? (
                    <div className="requestor-info-row__search">
                        {matchState.status === "noMatch" ? (
                            <span className="requestor-info-row__no-match">No match found</span>
                        ) : null}
                        {matchState.status === "serviceUnavailable" ? (
                            <span className="requestor-info-row__service-unavailable">Match service unavailable</span>
                        ) : null}
                        {props.isDdsRequest ? (
                            // Spec #35136 (F1f) fix: Classic's DDS search bundles Site Code,
                            // Fax, and Email (and more) into one call - not site-code-only - so
                            // offer all three here too, pre-filled with the row's current
                            // values, keeping Reset's reveal-then-search two-step consistent
                            // with every other row.
                            //
                            // AB#35252/AB#35251: same fax mask + length caps as the non-DDS
                            // branch below - this inline search box is the same shared
                            // component, so it needs the same constraints regardless of which
                            // fields it's showing.
                            <InlineSearchForm
                                key={`${props.currentDdsSiteCode ?? ""}|${props.extractedFax ?? ""}|${props.extractedEmail ?? ""}`}
                                className="requestor-info-row__inline-search"
                                fields={[
                                    { name: "siteCode", label: "Site Code", value: props.currentDdsSiteCode, tag: "PRIMARY",
                                      // AB#35251: was the one inline-search box with no cap at all, while its
                                      // Fax/Email siblings were capped. SiteCode is varchar(10).
                                      maxLength: DDS_SITE_CODE_MAX_LENGTH },
                                    { name: "fax", label: "Fax", value: props.extractedFax, type: "phone", maxLength: QUICK_ENTRY_FIELD_MAX_LENGTH.requestorFax },
                                    { name: "email", label: "Email", value: props.extractedEmail, maxLength: QUICK_ENTRY_FIELD_MAX_LENGTH.requestorEmail },
                                ]}
                                onSearch={onInlineDdsSearch}
                                onCancel={searchMode ? onInlineCancel : undefined}
                            />
                        ) : (
                            <InlineSearchForm
                                // AB#39478 review: InlineSearchForm's values/advancedOpen are mount-only,
                                // so the key must cover every extracted value that seeds them - this was
                                // missing the four advanced-search fields, leaving stale pre-fill behind
                                // when a document replace changes one of them but not fax/email.
                                key={`${props.extractedFax ?? ""}|${props.extractedEmail ?? ""}|${props.extractedAddress1 ?? ""}|${props.extractedZip ?? ""}|${props.extractedName ?? ""}|${extractedContactName ?? ""}`}
                                className="requestor-info-row__inline-search"
                                fields={[
                                    // AB#35252: Fax masks on entry via the shared sanitizeFieldInput
                                    // ("phone" -> normalizePhone, same pathway as the add-new-contact
                                    // and inline edit forms), so letters and specials are rejected.
                                    // AB#35251: capped at the column-derived length - fax's 12 is a
                                    // complete XXX-XXX-XXXX. Email stays plain text (needs letters
                                    // and "@") but is still capped.
                                    { name: "fax", label: "Fax", value: props.extractedFax, tag: "PRIMARY", type: "phone", maxLength: QUICK_ENTRY_FIELD_MAX_LENGTH.requestorFax },
                                    { name: "email", label: "Email", value: props.extractedEmail, tag: "PRIMARY", maxLength: QUICK_ENTRY_FIELD_MAX_LENGTH.requestorEmail },
                                ]}
                                // AB#39478: Classic's Advanced Search set, pre-filled from the
                                // extraction where the document gave us a value (dev, 2026-09-17).
                                // Review: the section starts collapsed and stays that way - matches
                                // Classic, which gates purely on open/closed (see InlineSearchForm).
                                // Caps are Classic's own search caps, not the column widths.
                                advancedFields={[
                                    { name: "address", label: "Address", value: props.extractedAddress1, maxLength: REQUESTOR_SEARCH_MAX_LENGTH.address },
                                    { name: "zip", label: "Zip Code", value: props.extractedZip, maxLength: REQUESTOR_SEARCH_MAX_LENGTH.zip },
                                    { name: "requestorName", label: "Requestor Name", value: props.extractedName, maxLength: REQUESTOR_SEARCH_MAX_LENGTH.requestorName },
                                    { name: "contactName", label: "Contact", value: extractedContactName, maxLength: REQUESTOR_SEARCH_MAX_LENGTH.contactName },
                                ]}
                                onSearch={onInlineSearch}
                                onCancel={searchMode ? onInlineCancel : undefined}
                            />
                        )}
                        {/* Review: nested here (not a sibling of showInlineSearch) so a refusal
                            message cannot survive onto a resolved row - it's only ever meaningful
                            while this search form is still up, regardless of which path resolved
                            the row (Cancel, a successful search, or the automated match effect). */}
                        {searchError ? (
                            <div className="requestor-info-row__contact-required">
                                <span className="error-message">{searchError}</span>
                            </div>
                        ) : null}
                    </div>
                ) : (
                    renderValue()
                )}
                {/* Inside qe-row__value, not the right-hand qe-row__action column, so the
                    three controls line up with the detail list's left edge rather than
                    floating out to the right of the row. Randy 2026-07-27. */}
                {matchState.status === "matched" && matchState.confidence === "single" && !searchMode && !hasContact && props.onAssignContact ? (
                    <div className="requestor-info-row__contact-required">
                        <span className="error-message">{CONTACT_SELECTION_REQUIRED_MESSAGE}</span>
                    </div>
                ) : null}
                {/* AB#35119: shown whenever a save was refused, independent of match state - the
                    refusal is about the contact's values, not about how the requestor was matched. */}
                {props.contactSaveError ? (
                    <div className="requestor-info-row__contact-required">
                        <span className="error-message">{props.contactSaveError}</span>
                    </div>
                ) : null}
                {matchState.status === "matched" && matchState.confidence === "single" && !searchMode ? (
                    <div className="qe-row__action requestor-info-row__actions">
                        {/* Assign Contact sits with Search: both act on the resolved requestor. */}
                        {renderAssignContact(hasContact)}
                        {/* AB#38736: [Edit] removed - a resolved requestor and its contact are not
                            editable. Search is the old Reset: back to the search box. */}
                        <button type="button" className="qe-btn requestor-info-row__search-btn" onClick={() => setSearchMode(true)}>
                            Search
                        </button>
                    </div>
                ) : null}
            </div>
            {modalOpen ? (
                <MatchGridModal
                    title="Requestor Search"
                    subtitle="Select a matching requestor to use for this request, or click Create New Requestor to add one."
                    columns={gridColumns}
                    rows={gridRows}
                    onSelect={onModalSelect}
                    createNewLabel="+ Create New Requestor"
                    onCreateNew={onOpenCreate}
                    onClose={() => setModalOpen(false)}
                />
            ) : null}
            {createOpen ? (
                <CreateEntityFormModal
                    title="Create New Requestor"
                    // AB#38824: see PatientInfoRow - the subtitle must agree with whether the
                    // form actually carried the document's values forward.
                    subtitle={carriesDocumentValues
                        ? "Review the details captured from the document, edit as needed, then Save. The requestor is saved with the transaction."
                        : "Enter the new requestor's details, then Save. The requestor is saved with the transaction."}
                    fields={createFields}
                    submitLabel="Save Requestor"
                    onSubmit={onCreateSubmit}
                    onClose={() => setCreateOpen(false)}
                />
            ) : null}
        </div>
    );
};

export default RequestorInfoRow;

/** Maps the current match state into the generic Quick Entry row-list contract (AC15/AC17). */
export function mapRequestorInfoToFieldGroupRow(matchState: RequestorMatchState, badgeNumber: number): FieldGroupRow {
    let status: FieldGroupRowStatus;
    switch (matchState.status) {
        case "awaitingDocument":
            status = FieldGroupRowStatus.EmptyNotRequired;
            break;
        case "matching":
            status = FieldGroupRowStatus.LowConfidence;
            break;
        case "matched":
            status = matchState.confidence === "single" ? FieldGroupRowStatus.Confirmed : FieldGroupRowStatus.LowConfidence;
            break;
        default:
            status = FieldGroupRowStatus.NeedsEntry;
            break;
    }

    return {
        id: REQUESTOR_INFO_ROW_ID,
        badgeNumber,
        status,
        label: "Requestor Info",
        value: null,
        // A created/user-entered value gets the blue "user" badge, not the green
        // AI-confirmed check (QuickEntryFieldGroupList AC).
        isAiPreSelected: matchState.matched && !matchState.userEntered,
    };
}
